/**
 * Credits bootstrap — bind @jubit-ai/credits-client to this app's Supabase
 * client + user store (Sprint 30).
 *
 * ADR-0331: this is a client of the shared `public.user_balance` ledger
 * owned by jubit-ai-universe. Do not mint a second wallet table here.
 */

import {
  createCreditsHooks,
  createCreditsService,
} from '@jubit-ai/credits-client';
import { getSupabase } from '../auth/supabase';
import { useUserStore } from '../state/userStore';

const supabase = getSupabase();

if (!supabase) {
  console.warn('[credits] No Supabase client — env vars unset. Returning stubs.');
}

const stubService = {
  grantCredits: async () => null,
  spendCredits: async () => ({ status: 'error' as const, message: 'no_supabase' }),
  getUserBalance: async () => ({ creditsAvailable: 0, totalEarned: 0, totalSpent: 0 }),
};

export const credits = supabase
  ? createCreditsService(supabase as unknown as Parameters<typeof createCreditsService>[0])
  : stubService;

const hooks = supabase
  ? createCreditsHooks({
      supabase: supabase as unknown as Parameters<typeof createCreditsHooks>[0]['supabase'],
      useAuthState: () => {
        const userId = useUserStore((s) => s.userId);
        return { user: userId ? { id: userId } : null };
      },
    })
  : null;

export const useCreditBalance = hooks
  ? hooks.useCreditBalance
  : (() => ({ balance: { creditsAvailable: 0, totalEarned: 0, totalSpent: 0 }, isLoading: false, refresh: () => {} }));

export const useImageUnlock = hooks
  ? hooks.useImageUnlock
  : (() => ({
      lockedModels: [],
      totalCost: 0,
      canAfford: true,
      executeSpend: async () => true,
    }));

export const invalidateCreditBalanceCache = hooks
  ? hooks.invalidateCreditBalanceCache
  : (() => {});

export {
  SPEND_COST,
  SHARE_BONUS_AMOUNT,
  ARENA_SESSION_REWARD,
  ONBOARDING_AMOUNT,
  EMPTY_BALANCE,
  canAccessTier,
  canAccessModel,
} from '@jubit-ai/credits-client';

export type {
  CreditBalance,
  GrantOptions,
  SpendResult,
} from '@jubit-ai/credits-client';
